/**
 * Progression helpers for the Adversarial Robustness Red-Team game.
 *
 * These functions are pure and deterministic. They convert the current game
 * state into mission progress + a lightweight score/rank layer to make play
 * more game-like without changing any structural rules.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { GameState } from "./types.js";
import { coverage } from "./rules.js";

export interface MissionStatus {
  readonly id: "foothold" | "confinement-witness" | "coverage";
  readonly title: string;
  readonly detail: string;
  readonly progress: number;
  readonly completed: boolean;
}

export interface ProgressionSummary {
  readonly score: number;
  readonly rank: "Recon" | "Probe Operator" | "Surface Mapper" | "Containment Cartographer";
  readonly coverageRatio: number;
  readonly missions: readonly MissionStatus[];
}

export function deriveProgression(
  state: Pick<GameState, "compromised" | "realized">,
  reachable: number,
): ProgressionSummary {
  const coverageRatio = coverage(reachable, state.realized.length);
  const score =
    state.compromised.length * 12 +
    state.realized.length * 25 +
    Math.round(coverageRatio * 100);

  const missions: readonly MissionStatus[] = [
    {
      id: "foothold",
      title: "First foothold",
      detail: "Compromise at least one agent.",
      progress: Math.min(1, state.compromised.length),
      completed: state.compromised.length >= 1,
    },
    {
      id: "confinement-witness",
      title: "Confinement witness",
      detail: "Realize two confined forbidden squares.",
      progress: Math.min(1, state.realized.length / 2),
      completed: state.realized.length >= 2,
    },
    {
      id: "coverage",
      title: "Surface cartography",
      detail: "Map 60% of the currently reachable surface.",
      progress:
        reachable <= 0 ? 0 : Math.min(1, coverageRatio / 0.6),
      completed: reachable > 0 && coverageRatio >= 0.6,
    },
  ];

  return {
    score,
    rank: deriveRank(score, coverageRatio),
    coverageRatio,
    missions,
  };
}

function deriveRank(score: number, coverageRatio: number): ProgressionSummary["rank"] {
  if (score >= 150 || coverageRatio >= 0.85) {
    return "Containment Cartographer";
  }
  if (score >= 90 || coverageRatio >= 0.6) {
    return "Surface Mapper";
  }
  if (score >= 35) {
    return "Probe Operator";
  }
  return "Recon";
}
